import { useState, useEffect } from 'react';
import {
  X, ChevronRight, ChevronLeft, Check, Shield, Bell,
  Wifi, Activity, Clock, TrendingDown, Users, Server, Zap,
  XCircle, AlertTriangle, Settings, Eye, Send, Link2, Hash} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';

const STEPS = [
  { id: 'condition', label: 'Condition', icon: AlertTriangle },
  { id: 'parameters', label: 'Parameters', icon: Settings },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'review', label: 'Review', icon: Check }
];

const CONDITION_TYPES = [
  { value: 'DEVICE_OFFLINE', label: 'Device Offline', icon: XCircle, color: '#ef4444',
    description: 'Alert when a device is unreachable for a specified duration',
    params: [{ key: 'thresholdSeconds', label: 'Offline Duration', unit: 'seconds', type: 'number', default: 120, min: 30 }] },
  { value: 'DEVICE_FLAPPING', label: 'Device Flapping', icon: Zap, color: '#f59e0b',
    description: 'Alert when a device toggles online/offline repeatedly',
    params: [
      { key: 'toggleCount', label: 'Toggle Count', type: 'number', default: 3, min: 2 },
      { key: 'windowMinutes', label: 'Time Window', unit: 'minutes', type: 'number', default: 10, min: 1 },
    ] },
  { value: 'LATENCY_THRESHOLD', label: 'High Latency', icon: Clock, color: '#f59e0b',
    description: 'Alert when device response time exceeds a threshold',
    params: [{ key: 'thresholdMs', label: 'Latency Threshold', unit: 'ms', type: 'number', default: 200, min: 10 }] },
  { value: 'LATENCY_SPIKE', label: 'Latency Spike', icon: TrendingDown, color: '#a855f7',
    description: 'Alert when latency spikes above the rolling average',
    params: [
      { key: 'multiplier', label: 'Spike Multiplier', unit: '×', type: 'number', default: 3, min: 1.5, step: 0.5 },
      { key: 'baselineHours', label: 'Baseline Window', unit: 'hours', type: 'number', default: 24, min: 1 },
    ] },
  { value: 'GROUP_PERCENT_OFFLINE', label: 'Group Failure', icon: Users, color: '#ef4444',
    description: 'Alert when a percentage of devices in a group go offline',
    params: [
      { key: 'group', label: 'Device Group', type: 'text', default: '', placeholder: 'e.g. Wave Cameras' },
      { key: 'percentThreshold', label: 'Offline Threshold', unit: '%', type: 'number', default: 50, min: 10, max: 100 },
    ] },
  { value: 'MULTI_DEVICE_OFFLINE', label: 'Multi-Device Offline', icon: Server, color: '#ef4444',
    description: 'Alert when multiple devices go offline simultaneously',
    params: [{ key: 'minDevices', label: 'Minimum Devices', type: 'number', default: 3, min: 2 }] },
  { value: 'AGENT_DISCONNECTED', label: 'Agent Disconnected', icon: Wifi, color: '#6b7280',
    description: 'Alert when all monitoring agents lose connection',
    params: [{ key: 'thresholdSeconds', label: 'Disconnect Duration', unit: 'seconds', type: 'number', default: 120, min: 30 }] },
  { value: 'UPTIME_TRENDING_DOWN', label: 'Uptime Declining', icon: TrendingDown, color: '#f97316',
    description: 'Alert when device uptime drops below a threshold over time',
    params: [
      { key: 'uptimeThreshold', label: 'Uptime Threshold', unit: '%', type: 'number', default: 95, min: 50, max: 100 },
      { key: 'windowDays', label: 'Time Window', unit: 'days', type: 'number', default: 7, min: 1 },
    ] },
];

const SEVERITY_OPTIONS = [
  { value: 'INFO', label: 'Info', color: '#3b82f6', description: 'Low priority, informational' },
  { value: 'WARNING', label: 'Warning', color: '#f59e0b', description: 'Medium priority, needs attention' },
  { value: 'CRITICAL', label: 'Critical', color: '#ef4444', description: 'High priority, immediate action required' },
];

const DEVICE_TYPES = [
  { value: '', label: 'Any Device Type' },
  { value: 'CAMERA', label: 'Camera' },
  { value: 'NVR', label: 'NVR' },
  { value: 'SERVER', label: 'Server' },
  { value: 'ROUTER', label: 'Router' },
  { value: 'SWITCH', label: 'Switch' },
  { value: 'ACCESS_CONTROLLER', label: 'Access Controller' },
  { value: 'INTERCOM', label: 'Intercom' },
];

const AlertRuleWizard = ({ onClose, onComplete, editingRule = null }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    conditionType: 'DEVICE_OFFLINE',
    conditionParams: { thresholdSeconds: 120 },
    deviceFilter: {},
    severity: 'WARNING',
    cooldownSeconds: 300,
    notifyInApp: true,
    notifyEmail: true,
    notifyPush: false,
    notifyWebhook: false,
    webhookUrl: '',
    notifySlack: false,
    slackWebhookUrl: '',
    enabled: true,
    quietHoursStart: '',
    quietHoursEnd: '',
    quietHoursDays: [],
  });

  useEffect(() => {
    if (editingRule) {
      setForm({
        name: editingRule.name || '',
        description: editingRule.description || '',
        conditionType: editingRule.conditionType || 'DEVICE_OFFLINE',
        conditionParams: editingRule.conditionParams || {},
        deviceFilter: editingRule.deviceFilter || {},
        severity: editingRule.severity || 'WARNING',
        cooldownSeconds: editingRule.cooldownSeconds || 300,
        notifyInApp: editingRule.notifyInApp ?? true,
        notifyEmail: editingRule.notifyEmail ?? false,
        notifyPush: editingRule.notifyPush ?? false,
        notifyWebhook: editingRule.notifyWebhook ?? false,
        webhookUrl: editingRule.webhookUrl || '',
        notifySlack: editingRule.notifySlack ?? false,
        slackWebhookUrl: editingRule.slackWebhookUrl || '',
        enabled: editingRule.enabled ?? true,
        quietHoursStart: editingRule.quietHoursStart || '',
        quietHoursEnd: editingRule.quietHoursEnd || '',
        quietHoursDays: editingRule.quietHoursDays || [],
      });
    }
  }, [editingRule]);

  const currentCondition = CONDITION_TYPES.find(c => c.value === form.conditionType);

  const handleConditionSelect = (type) => {
    const config = CONDITION_TYPES.find(c => c.value === type);
    const defaultParams = {};
    (config?.params || []).forEach(p => { defaultParams[p.key] = p.default; });
    setForm(f => ({ ...f, conditionType: type, conditionParams: defaultParams }));
  };

  const updateParam = (key, value, type) => {
    setForm(f => ({
      ...f,
      conditionParams: {
        ...f.conditionParams,
        [key]: type === 'number' ? Number(value) : value
      }
    }));
  };

  const updateFilter = (key, value) => {
    setForm(f => ({
      ...f,
      deviceFilter: { ...f.deviceFilter, [key]: value || undefined }
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!form.conditionType;
      case 1: return !!form.name.trim();
      case 2: return true;
      case 3: return true;
      default: return false;
    }
  };

  const goNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed()) setCurrentStep(currentStep + 1);
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    setSaving(true);
    try {
      const url = editingRule ? `/api/md/rules/${editingRule.id}` : '/api/md/rules';
      const method = editingRule ? 'PUT' : 'POST';
      const res = await fetchWithTenant(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(editingRule ? 'Rule updated successfully' : 'Rule created successfully');
        onComplete?.();
        onClose();
      } else {
        toast.error(data.error || 'Failed to save rule');
      }
    } catch (err) {
      toast.error('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  // Step 1: Select Condition Type
  const renderStepCondition = () => (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">
        Select the condition that will trigger this alert rule.
      </p>
      <div className="alert-condition-grid">
        {CONDITION_TYPES.map(ct => {
          const Icon = ct.icon;
          const isSelected = form.conditionType === ct.value;
          return (
            <div
              key={ct.value}
              className={`alert-condition-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleConditionSelect(ct.value)}
              style={isSelected ? { borderColor: ct.color } : {}}
            >
              <div className="alert-condition-icon" style={{ color: ct.color }}>
                <Icon size={24} />
              </div>
              <div className="alert-condition-info">
                <div className="alert-condition-label">{ct.label}</div>
                <div className="alert-condition-desc">{ct.description}</div>
              </div>
              {isSelected && (
                <div className="alert-condition-check" style={{ background: ct.color }}>
                  <Check size={12} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Step 2: Configure Parameters
  const renderStepParameters = () => (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">
        Configure the rule details and threshold parameters for <strong>{currentCondition?.label}</strong>.
      </p>
      
      {/* Rule Name & Description */}
      <div className="wizard-field">
        <label className="wizard-label">Rule Name *</label>
        <input
          type="text"
          className="wizard-input"
          placeholder="e.g. Camera Offline > 2min"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        />
      </div>
      
      <div className="wizard-field">
        <label className="wizard-label">Description</label>
        <input
          type="text"
          className="wizard-input"
          placeholder="Optional description of what this rule monitors"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        />
      </div>

      {/* Condition Parameters */}
      {currentCondition?.params?.length > 0 && (
        <div className="wizard-params-section">
          <label className="wizard-label"><Settings size={14} /> Threshold Parameters</label>
          <div className="wizard-params-grid">
            {currentCondition.params.map(p => (
              <div key={p.key} className="wizard-param-field">
                <label>{p.label}</label>
                <div className="wizard-param-input-wrap">
                  <input
                    type={p.type}
                    className="wizard-input"
                    placeholder={p.placeholder || ''}
                    value={form.conditionParams[p.key] ?? p.default}
                    min={p.min}
                    max={p.max}
                    step={p.step || 1}
                    onChange={e => updateParam(p.key, e.target.value, p.type)}
                  />
                  {p.unit && <span className="wizard-param-unit">{p.unit}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Device Filter */}
      <div className="wizard-params-section">
        <label className="wizard-label"><Eye size={14} /> Device Filter <span className="wizard-hint">(optional - leave empty for all devices)</span></label>
        <div className="wizard-params-grid">
          <div className="wizard-param-field">
            <label>Device Type</label>
            <select
              className="wizard-select"
              value={form.deviceFilter.deviceType || ''}
              onChange={e => updateFilter('deviceType', e.target.value)}
            >
              {DEVICE_TYPES.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>
          <div className="wizard-param-field">
            <label>Group</label>
            <input
              type="text"
              className="wizard-input"
              placeholder="e.g. Wave Cameras"
              value={form.deviceFilter.group || ''}
              onChange={e => updateFilter('group', e.target.value)}
            />
          </div>
          <div className="wizard-param-field">
            <label>IP Prefix</label>
            <input
              type="text"
              className="wizard-input"
              placeholder="e.g. 192.168.1"
              value={form.deviceFilter.ipPrefix || ''}
              onChange={e => updateFilter('ipPrefix', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Severity & Cooldown */}
      <div className="wizard-params-section">
        <label className="wizard-label"><AlertTriangle size={14} /> Severity & Cooldown</label>
        <div className="wizard-severity-grid">
          {SEVERITY_OPTIONS.map(sev => (
            <button
              key={sev.value}
              type="button"
              className={`wizard-severity-card ${form.severity === sev.value ? 'selected' : ''}`}
              onClick={() => setForm(f => ({ ...f, severity: sev.value }))}
              style={form.severity === sev.value ? { borderColor: sev.color } : {}}
            >
              <div className="wizard-severity-dot" style={{ background: sev.color }} />
              <strong>{sev.label}</strong>
              <span>{sev.description}</span>
            </button>
          ))}
        </div>
        <div className="wizard-field" style={{ marginTop: '16px' }}>
          <label className="wizard-label">Cooldown Period</label>
          <div className="wizard-param-input-wrap" style={{ maxWidth: '200px' }}>
            <input
              type="number"
              className="wizard-input"
              value={form.cooldownSeconds}
              min={0}
              onChange={e => setForm(f => ({ ...f, cooldownSeconds: Number(e.target.value) }))}
            />
            <span className="wizard-param-unit">seconds</span>
          </div>
          <span className="wizard-hint">Minimum time between repeat alerts for the same device</span>
        </div>
      </div>
    </div>
  );

  // Step 3: Notifications
  const renderStepNotifications = () => (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">
        Choose how you want to be notified when this alert triggers.
      </p>
      
      <div className="wizard-notify-grid">
        {[
          { key: 'notifyInApp', label: 'In-App', icon: Bell, desc: 'Show in the Alerts page' },
          { key: 'notifyEmail', label: 'Email', icon: Send, desc: 'Send to users with Manage Alerts permission' },
          { key: 'notifyPush', label: 'Push', icon: Zap, desc: 'Browser push notification' },
          { key: 'notifySlack', label: 'Slack', icon: Hash, desc: 'Send to Slack channel via webhook' },
          { key: 'notifyWebhook', label: 'Webhook', icon: Link2, desc: 'POST JSON to any URL' },
        ].map(ch => (
          <label
            key={ch.key}
            className={`wizard-notify-card ${form[ch.key] ? 'active' : ''}`}
          >
            <input
              type="checkbox"
              checked={form[ch.key]}
              onChange={e => setForm(f => ({ ...f, [ch.key]: e.target.checked }))}
            />
            <ch.icon size={20} />
            <div className="wizard-notify-info">
              <strong>{ch.label}</strong>
              <span>{ch.desc}</span>
            </div>
            <div className="wizard-notify-check">
              {form[ch.key] && <Check size={14} />}
            </div>
          </label>
        ))}
      </div>

      {form.notifySlack && (
        <div className="wizard-field" style={{ marginTop: '16px' }}>
          <label className="wizard-label"><Hash size={14} /> Slack Webhook URL</label>
          <input
            type="url"
            className="wizard-input"
            placeholder="https://hooks.slack.com/services/T.../B.../..."
            value={form.slackWebhookUrl}
            onChange={e => setForm(f => ({ ...f, slackWebhookUrl: e.target.value }))}
          />
          <span className="wizard-hint">Create an Incoming Webhook in your Slack workspace settings</span>
        </div>
      )}

      {form.notifyWebhook && (
        <div className="wizard-field" style={{ marginTop: '16px' }}>
          <label className="wizard-label"><Link2 size={14} /> Webhook URL</label>
          <input
            type="url"
            className="wizard-input"
            placeholder="https://your-endpoint.com/webhook"
            value={form.webhookUrl}
            onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))}
          />
          <span className="wizard-hint">Alert data will be POSTed as JSON to this URL</span>
        </div>
      )}

      {/* Quiet Hours */}
      <div className="wizard-params-section" style={{ marginTop: '24px' }}>
        <label className="wizard-label"><Clock size={14} /> Quiet Hours <span className="wizard-hint">(optional - suppress alerts during these times)</span></label>
        <div className="wizard-params-grid">
          <div className="wizard-param-field">
            <label>Start Time</label>
            <input
              type="time"
              className="wizard-input"
              value={form.quietHoursStart}
              onChange={e => setForm(f => ({ ...f, quietHoursStart: e.target.value }))}
            />
          </div>
          <div className="wizard-param-field">
            <label>End Time</label>
            <input
              type="time"
              className="wizard-input"
              value={form.quietHoursEnd}
              onChange={e => setForm(f => ({ ...f, quietHoursEnd: e.target.value }))}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Step 4: Review
  const renderStepReview = () => {
    const CondIcon = currentCondition?.icon || AlertTriangle;
    const sevOption = SEVERITY_OPTIONS.find(s => s.value === form.severity);
    const notifyChannels = [
      form.notifyInApp && 'In-App',
      form.notifyEmail && 'Email',
      form.notifyPush && 'Push',
      form.notifySlack && 'Slack',
      form.notifyWebhook && 'Webhook',
    ].filter(Boolean);

    return (
      <div className="wizard-step-content">
        <p className="wizard-step-desc">
          Review your alert rule configuration before {editingRule ? 'updating' : 'creating'}.
        </p>

        <div className="wizard-summary">
          {/* Rule Info */}
          <div className="wizard-summary-section">
            <h4><Shield size={16} /> Rule Details</h4>
            <div className="wizard-summary-row">
              <span>Name</span>
              <strong>{form.name || 'Unnamed Rule'}</strong>
            </div>
            {form.description && (
              <div className="wizard-summary-row">
                <span>Description</span>
                <strong>{form.description}</strong>
              </div>
            )}
            <div className="wizard-summary-row">
              <span>Status</span>
              <span className={`wizard-badge ${form.enabled ? 'success' : 'muted'}`}>
                {form.enabled ? 'ENABLED' : 'DISABLED'}
              </span>
            </div>
          </div>

          {/* Condition */}
          <div className="wizard-summary-section">
            <h4><CondIcon size={16} style={{ color: currentCondition?.color }} /> Condition</h4>
            <div className="wizard-summary-row">
              <span>Type</span>
              <strong>{currentCondition?.label}</strong>
            </div>
            {currentCondition?.params?.map(p => (
              <div key={p.key} className="wizard-summary-row">
                <span>{p.label}</span>
                <strong>{form.conditionParams[p.key]}{p.unit ? ` ${p.unit}` : ''}</strong>
              </div>
            ))}
          </div>

          {/* Device Filter */}
          {Object.keys(form.deviceFilter).some(k => form.deviceFilter[k]) && (
            <div className="wizard-summary-section">
              <h4><Eye size={16} /> Device Filter</h4>
              {form.deviceFilter.deviceType && (
                <div className="wizard-summary-row">
                  <span>Device Type</span>
                  <strong>{form.deviceFilter.deviceType}</strong>
                </div>
              )}
              {form.deviceFilter.group && (
                <div className="wizard-summary-row">
                  <span>Group</span>
                  <strong>{form.deviceFilter.group}</strong>
                </div>
              )}
              {form.deviceFilter.ipPrefix && (
                <div className="wizard-summary-row">
                  <span>IP Prefix</span>
                  <strong>{form.deviceFilter.ipPrefix}</strong>
                </div>
              )}
            </div>
          )}

          {/* Severity & Notifications */}
          <div className="wizard-summary-section">
            <h4><Bell size={16} /> Alerts & Notifications</h4>
            <div className="wizard-summary-row">
              <span>Severity</span>
              <span className="wizard-badge" style={{ background: `${sevOption?.color}22`, color: sevOption?.color, borderColor: sevOption?.color }}>
                {form.severity}
              </span>
            </div>
            <div className="wizard-summary-row">
              <span>Cooldown</span>
              <strong>{form.cooldownSeconds} seconds</strong>
            </div>
            <div className="wizard-summary-row">
              <span>Channels</span>
              <strong>{notifyChannels.join(', ') || 'None'}</strong>
            </div>
            {form.quietHoursStart && form.quietHoursEnd && (
              <div className="wizard-summary-row">
                <span>Quiet Hours</span>
                <strong>{form.quietHoursStart} - {form.quietHoursEnd}</strong>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderStepCondition();
      case 1: return renderStepParameters();
      case 2: return renderStepNotifications();
      case 3: return renderStepReview();
      default: return null;
    }
  };

  return (
    <div className="wizard-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wizard-modal" style={{ maxWidth: '720px' }}>
        <div className="wizard-header">
          <div className="wizard-header-left">
            <Shield size={20} />
            <h2>{editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}</h2>
          </div>
          <button className="wizard-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="wizard-stepper">
          {STEPS.map((step, idx) => (
            <div
              key={step.id}
              className={`wizard-stepper-item ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
              onClick={() => { if (idx < currentStep) setCurrentStep(idx); }}
            >
              <div className="wizard-stepper-dot">
                {idx < currentStep ? <Check size={12} /> : <step.icon size={12} />}
              </div>
              <span className="wizard-stepper-label">{step.label}</span>
            </div>
          ))}
        </div>

        <div className="wizard-body">
          {renderCurrentStep()}
        </div>

        <div className="wizard-footer">
          <div className="wizard-footer-left">
            {currentStep > 0 && (
              <button className="wizard-btn secondary" onClick={goBack}>
                <ChevronLeft size={16} /> Back
              </button>
            )}
          </div>
          <div className="wizard-footer-right">
            {currentStep < STEPS.length - 1 ? (
              <button className="wizard-btn primary" onClick={goNext} disabled={!canProceed()}>
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button className="wizard-btn approve" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <><Activity size={16} className="spin" /> Saving...</>
                ) : (
                  <><Check size={16} /> {editingRule ? 'Update Rule' : 'Create Rule'}</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertRuleWizard;
